/**
 * Shared paste helper with conflict resolution.
 *
 * Used by both DriveView and WorkspaceExplorer for consistent paste UX.
 * Checks for name conflicts, shows the conflict dialog, then copies files.
 */
import { filesApi, type ConflictStrategy } from './index'
import type { ConflictResolution, ConflictOperation, ConflictInfo } from '@papan/core-ui/ui'
import type { PinnedBatch } from '@papan/api-client'

export interface PasteItem {
  id: string
  name: string
  isFolder: boolean
  parentId: string | null
}

export interface PasteOptions {
  /** Operation tracking ID for progress events. */
  operationId?: string
  /**
   * If set, dispatches to /api/files/cross-tenant-copy instead of
   * /api/files/copy. Required when source items live in a different
   * tenant than the destination — the SPA detects this from the
   * clipboard's `tenantId` vs the current tenant.
   */
  crossTenant?: {
    sourceTenantId: string
    /**
     * The user's effective email in the SOURCE tenant at copy time.
     * The server uses it to pick the right membership when the user
     * has multiple emails in the source tenant. See
     * filesApi.crossTenantCopy for the multi-email-leak rationale.
     */
    sourceOrgEmail?: string
  }
  /**
   * A copy ACROSS tenants holding a `.shared` / `.chain` the user can read asks whether
   * to keep them as links or copy the content they point to (owner ruling 2026-09-27 —
   * only cross-tenant copies ask). Resolve 'files' (keep links), 'contents', or null to
   * cancel the paste. Absent: keep links without asking.
   */
  askPointers?: (label: string) => Promise<'files' | 'contents' | null>
  /**
   * #1316 review round 2 (owner, 2026-10-28): the paste the person started (`pinnedBatch()`) — every request is sent as
   * the identity that started it, so a tenant / identity switch mid-paste never sends the copy as the next session.
   * Once that session ended nothing is ASKED: a conflicting item is skipped, a pointer question cancels the paste.
   */
  batch?: PinnedBatch
}

/**
 * Copy items to a destination folder with interactive conflict resolution.
 *
 * Returns both the copied list (id + name) AND the per-source errors the
 * server reported. Callers must surface non-empty `errors` to the user;
 * previously the API caught per-source failures silently and the
 * unique-name crash on the slow path disappeared into `copied: []` with
 * no toast.
 *
 * Cross-tenant copy goes through the same conflict-resolution dance
 * (this was missing before — the cross-tenant branch in DriveView
 * called the API directly and the server's getUniqueName silently
 * auto-renamed conflicting items, so users got `Folder (1)` at the
 * destination without ever being asked).
 */
export async function pasteItems(
  items: PasteItem[],
  destinationId: string | null,
  operation: ConflictOperation,
  resolveConflict: (conflict: ConflictInfo) => Promise<{ resolution: ConflictResolution; applyToAll: boolean }>,
  options?: PasteOptions,
): Promise<{
  copied: Array<{ id: string; name: string }>
    /** Existing files a Replace wrote a new version into (native ids; never in `copied`). */
    versioned?: Array<{ id: string; name: string; previousVersionId: string | null; versionId?: string }>
  errors: Array<{ sourceId: string; message: string }>
}> {
  if (items.length === 0) return { copied: [], errors: [] }
  const { operationId, crossTenant, askPointers, batch } = options ?? {}
  const pin = () => batch?.opts() ?? {}

  // Across tenants: keep links, or copy what they point to? Asked only when the copy
  // actually holds a pointer the user can read (the server answers).
  let expandPointers = false
  if (crossTenant && askPointers) {
    const any = await filesApi.crossTenantCopyHasPointers(
      crossTenant.sourceTenantId, items.map((i) => i.id), destinationId, crossTenant.sourceOrgEmail, pin(),
    ).catch(() => false)
    if (any) {
      if (batch?.ended()) return { copied: [], errors: [] }
      const choice = await askPointers(items.length === 1 ? items[0]!.name : `${items.length} items`)
      if (choice === null) return { copied: [], errors: [] }
      expandPointers = choice === 'contents'
    }
  }

  // Dispatch the actual copy. Centralized so the cross-tenant and
  // same-tenant paths share the same conflict-resolution dance above.
  async function copyDispatch(idsToInclude: string[], strategy: ConflictStrategy | undefined) {
    if (crossTenant) {
      const r = await filesApi.crossTenantCopy(
        crossTenant.sourceTenantId, idsToInclude, destinationId, crossTenant.sourceOrgEmail, strategy, operationId, expandPointers, pin(),
      )
      return { copied: r.copied, errors: r.errors ?? [], versioned: r.versioned ?? [] }
    }
    const r = await filesApi.copyFiles(idsToInclude, destinationId, strategy, operationId, pin())
    return { copied: r.copied, errors: r.errors ?? [], versioned: r.versioned ?? [] }
  }

  // Same-folder copy fast path: when pasting into the source's own
  // parent there is necessarily a name collision on every item, and
  // the user's intent is unambiguously "make a copy here". Skip the
  // dialog and let the server auto-rename. Cross-tenant by definition
  // can't be same-folder (different tenants), so this only fires for
  // same-tenant copy.
  const isSameFolder = !crossTenant && items.every(item => item.parentId === destinationId)
  if (isSameFolder) {
    return copyDispatch(items.map(i => i.id), 'keepBoth')
  }

  // Check for name conflicts in the destination folder. checkConflicts
  // queries the CURRENT tenant (destination), which is correct for
  // both same- and cross-tenant — the question is "does the
  // destination already have this name", regardless of where the
  // source lives.
  const conflicts = await filesApi.checkConflicts(
    items.map(i => ({ name: i.name, parentId: destinationId })),
    pin(),
  )
  const conflictNames = new Set(conflicts.map(c => c.name))

  // Resolve conflicts interactively
  let applyToAll: ConflictResolution | null = null
  const resolutions = new Map<string, ConflictStrategy>()

  for (const item of items) {
    if (conflictNames.has(item.name)) {
      // never a question in the session after the batch's own: the conflicting item is skipped
      if (batch?.ended()) { resolutions.set(item.id, 'skip'); continue }
      if (applyToAll) {
        resolutions.set(item.id, applyToAll)
      } else {
        const result = await resolveConflict({
          fileName: item.name,
          sourcePath: item.name,
          destPath: item.name,
          isFolder: item.isFolder,
          operation,
        })
        resolutions.set(item.id, result.resolution)
        if (result.applyToAll) applyToAll = result.resolution
      }
    }
  }

  // Filter out skipped items
  const idsToSkip = new Set(
    [...resolutions].filter(([, v]) => v === 'skip').map(([k]) => k)
  )
  const idsToInclude = items.filter(i => !idsToSkip.has(i.id)).map(i => i.id)
  if (idsToInclude.length === 0) return { copied: [], errors: [] }

  // Use the first non-skip resolution as the strategy for the batch.
  // Both /copy and /cross-tenant-copy honor the strategy server-side:
  // 'replace' writes a file as a new VERSION of the existing file — onto a `.chain` /
  // `.shared`, its new metadata through the link's rules (a folder is binned first — it
  // has no versions; for a folder the dialog says Merge),
  // 'keepBoth' (default) auto-renames via getUniqueName, 'skip' was
  // already filtered above.
  const strategy = [...resolutions.values()].find(v => v !== 'skip')
  return copyDispatch(idsToInclude, strategy)
}

/**
 * A paste's ONE undo step (owner, 2026-10-06): the items it created (deleted on undo) and the existing
 * files its Replace wrote a new version into, each with the version it replaced (restored on undo —
 * never deleted). `null` when the paste changed nothing undoable.
 */
export function pasteUndoRecord(result: {
  copied: Array<{ id: string }>
  versioned?: Array<{ id: string; previousVersionId: string | null; versionId?: string }>
}): {
  copiedFileIds: string[]
  versioned: Array<{ fileId: string; previousVersionId: string; versionId?: string }>
} | null {
  const copiedFileIds = result.copied.map((c) => c.id)
  // `versionId` = the version this Replace wrote: the undo restores only while it is still current.
  const versioned = (result.versioned ?? []).flatMap((v) => (v.previousVersionId ? [{ fileId: v.id, previousVersionId: v.previousVersionId, ...(v.versionId ? { versionId: v.versionId } : {}) }] : []))
  return copiedFileIds.length || versioned.length ? { copiedFileIds, versioned } : null
}
